import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { Link, useSearchParams } from "react-router-dom";
import SportArcSlider from "../components/SportArcSlider";
import AccessKeyModal from "../components/AccessKeyModal";
import EmojiRainIntro from "../components/EmojiRainIntro";

/* ================= FlyIn mit Rotation & Scaling, stark lebendig ================= */
const directions45deg = [
  { x: -75, y: 0, rotate: -14 },
  { x: 75, y: 0, rotate: 12 },
  { x: 0, y: -75, rotate: 7 },
  { x: 0, y: 75, rotate: -9 },
  { x: -55, y: -55, rotate: -18 },
  { x: 55, y: 55, rotate: 16 },
  { x: -55, y: 55, rotate: 24 },
  { x: 55, y: -55, rotate: -22 },
];

const createRandomVariant = () => {
  const d = directions45deg[Math.floor(Math.random() * directions45deg.length)];
  return {
    hidden: { opacity: 0, x: d.x, y: d.y, scale: 0.8, rotate: d.rotate, filter: "brightness(0.65)" },
    visible: {
      opacity: 1,
      x: 0,
      y: 0,
      scale: 1,
      rotate: 0,
      filter: "brightness(1)",
      transition: { type: "spring", stiffness: 230, damping: 27, mass: 0.8 },
    },
  };
};

/* ================= Haupt Onboarding Seite ================= */
const OnboardingPage = () => {
  const { t } = useTranslation();
  const { startSession } = useAuth();
  const [searchParams] = useSearchParams();

  const [nickname, setNickname] = useState("");
  const [selfie, setSelfie] = useState(null);
  const [selectedSport, setSelectedSport] = useState(null);
  const [currentSportTooltip, setCurrentSportTooltip] = useState(null);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showAccessKeyModal, setShowAccessKeyModal] = useState(false);

  // Intro Animation State
  const [showIntro, setShowIntro] = useState(false);

  useEffect(() => {
    // Prüfe ob Intro schon gesehen wurde
    const hasSeenIntro = localStorage.getItem('gonkle_intro_seen');
    if (!hasSeenIntro) {
      setShowIntro(true);
    }
  }, []);

  const handleIntroComplete = () => {
    localStorage.setItem('gonkle_intro_seen', 'true');
    setShowIntro(false);
  };

  useEffect(() => {
    if (searchParams.get("key")) setShowAccessKeyModal(true);
  }, [searchParams]);

  const isReady = nickname.trim() && selfie && selectedSport;

  const handleSubmit = async e => {
    e.preventDefault();
    if (!isReady || loading) return;
    setError("");
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("nickname", nickname.trim());
      formData.append("selfie", selfie);
      formData.append("favorite_sport", selectedSport);
      await startSession(formData);
    } catch (err) {
      setError(err?.toString() || "Unbekannter Fehler");
      setLoading(false);
    }
  };

  const containerStyle = {
    position: "relative", minHeight: "100vh",
    backgroundColor: "var(--color-background)",
    display: "flex", justifyContent: "center",
    alignItems: "center",
    padding: "2rem 1rem", userSelect: "none",
  };

  const cardStyle = {
    maxWidth: 420, margin: "auto", padding: "2rem 1.5rem",
    borderRadius: "var(--border-radius-large)", background: "var(--color-surface-elevated)",
    boxShadow: "var(--shadow-lg)", border: "1px solid var(--color-accent-dim)",
    color: "var(--color-text-primary)", textAlign: "center",
    width: "100%", fontFamily: "var(--font-display)", boxSizing: "border-box",
  };

  // formElements definieren
  const formElements = [
    { key: 'title', content: <h1 style={{ fontSize: "clamp(2.5rem, 8vw, 4rem)", marginBottom: 8, color: "var(--color-accent)", userSelect: "none", letterSpacing: "0.05em" }}>gonkle</h1> },
    { key: 'subtitle', content: <h2 style={{ fontWeight: "400", fontSize: "clamp(1.1rem, 3.5vw, 1.5rem)", marginBottom: 12, color: "var(--color-text-secondary)" }}>{t("app_subtitle")}</h2> },
    {
      key: 'nickname', content: <input type="text" placeholder={t("nickname_placeholder")} value={nickname} onChange={e => setNickname(e.target.value)} required autoComplete="off" spellCheck={false} style={{
        width: "100%", maxWidth: 320, padding: "14px 18px", fontSize: 18, borderRadius: "var(--border-radius-medium)",
        border: "1px solid var(--color-accent-dim)", backgroundColor: "var(--color-surface)", color: "var(--color-text-primary)",
        textAlign: "center", outline: "none",
      }} />
    },
    {
      key: 'selfie', content: <label style={{
        display: "block", padding: 24, backgroundColor: "var(--color-surface)", borderRadius: "var(--border-radius-medium)",
        border: "2px dashed var(--color-accent-dim)", cursor: "pointer", userSelect: "none", color: "var(--color-text-secondary)",
        margin: "auto", fontWeight: "600", fontSize: "0.95rem", marginBottom: 10, maxWidth: 320,
      }}>{selfie ? `📸 ${selfie.name.substring(0, 20)}...` : t("selfie_placeholder")}
        <input type="file" accept="image/*" capture="user" onChange={e => setSelfie(e.target.files[0])} style={{ display: "none" }} required />
      </label>
    },
    {
      key: 'sportSlider', content: <div style={{ width: "100%", maxWidth: 350, marginBottom: 10 }}>
        <p style={{ fontSize: "0.9rem", color: "var(--color-text-secondary)", marginBottom: 8, userSelect: "none" }}>{t("pick_vibe")}</p>
        <SportArcSlider onSelect={(val, label) => {
          setSelectedSport(val);
          setCurrentSportTooltip(label);
        }} initialSportKey={selectedSport} />
        {currentSportTooltip && <div style={{
          marginTop: 6, fontSize: 13, color: "var(--color-text-secondary)", userSelect: "none"
        }}>{currentSportTooltip}</div>}
      </div>
    },
    {
      key: 'submit', content: <button type="submit" disabled={!isReady || loading} style={{
        width: "100%", maxWidth: 320, padding: "16px 0", fontSize: 20, fontWeight: "bold", borderRadius: "var(--border-radius-medium)",
        background: isReady ? "var(--color-button-primary)" : "var(--color-button-secondary)", color: isReady ? "var(--color-background)" : "var(--color-text-muted)",
        border: "1px solid var(--color-accent)", cursor: isReady ? "pointer" : "not-allowed", opacity: isReady ? 1 : 0.5,
        transition: "all 0.2s ease", userSelect: "none",
      }}>{loading ? t("loading") : t("go_button")}</button>
    },
    {
      key: 'error', content: error ? <div style={{
        color: "var(--color-danger)", marginTop: 8, fontWeight: "600", fontSize: 13, userSelect: "text"
      }}>{error}</div> : null
    },
    {
      key: 'accessKey', content: <p role="button" tabIndex={0} onClick={() => setShowAccessKeyModal(true)} onKeyDown={e => { if (e.key === "Enter") setShowAccessKeyModal(true); }} style={{
        cursor: "pointer", marginTop: 12, fontSize: 12, color: "var(--color-accent)", textDecoration: "underline", userSelect: "none"
      }}>{t("got_an_access_key_link")}</p>
    }
  ];

  // Memoize die zufälligen Varianten nur einmal pro Komponentendurchlauf für jedes Formelement
  const variantsArray = useMemo(() => {
    // Array mit gleicher Länge wie formElements, enthält stabile random Variants
    return Array(formElements.length).fill(null).map(() => createRandomVariant());
  }, [formElements.length]);

  return (
    <div style={containerStyle}>
      <AnimatePresence>
        {showIntro && <EmojiRainIntro onComplete={handleIntroComplete} />}
      </AnimatePresence>

      <motion.form
        initial="hidden"
        animate="visible"
        variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.08, delayChildren: 0.15 } } }}
        onSubmit={handleSubmit}
        noValidate
        style={{ ...cardStyle, display: "flex", flexDirection: "column", gap: 14, alignItems: "center" }}
      >
        {formElements.map(({ key, content }, index) =>
          content ? React.cloneElement(content, { key, variants: variantsArray[index], initial: "hidden", animate: "visible" }) : null
        )}
      </motion.form>

      <AnimatePresence>
        {showAccessKeyModal && <AccessKeyModal onClose={() => setShowAccessKeyModal(false)} />}
      </AnimatePresence>

      <motion.footer initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }} style={{
        position: "fixed", bottom: 8, width: "100%", fontSize: 12, textAlign: "center",
        color: "var(--color-text-muted)", userSelect: "none", padding: 8,
        backgroundColor: "rgba(10, 12, 25, 0.25)", backdropFilter: "blur(3px)", fontFamily: "var(--font-display)"
      }}>
        <p style={{ margin: "0 0 4px 0" }}>
          <Link to="/impressum" style={{ color: "var(--color-text-muted)", userSelect: "none" }}>Impressum</Link> |{" "}
          <Link to="/datenschutz" style={{ color: "var(--color-text-muted)", userSelect: "none" }}>Datenschutz</Link>
        </p>
        <p style={{ margin: 0, fontSize: 11, color: "rgba(255, 255, 255, 0.4)" }}>(c) nopainsoft</p>
      </motion.footer>
    </div>
  );
};

export default OnboardingPage;
